import React, { useEffect, useState } from "react";
import { createCheckoutSession, inviteTeammate } from "../../services/server";

/**
 * Admin onboarding popup — two sequential steps:
 *   1. Add a payment card (Stripe SetupIntent + Elements)
 *   2. Invite teammates with role selection
 *
 * Step 2 is reached only after step 1 succeeds; broker can skip it.
 * Reopening the modal later (via "billing:open-setup" event) lands on
 * step 1 again — invite is treated as a once-per-onboarding action.
 *
 * Props:
 *   open       — boolean, mounts when true
 *   onClose    — () => void, called when broker dismisses
 *   onSuccess  — () => void, called after card saved (parent refetches billing-status)
 */
// `open` is destructured-renamed to `isOpen` because ESLint's
// no-restricted-globals rule treats the bare `open` identifier as
// shadowing `window.open`.
export default function BillingSetupModal({
  open: isOpen,
  reason = "missing",
  initialStep = "card",
  onClose,
  onSuccess,
}) {
  const [step, setStep] = useState(initialStep); // "card" | "invite"

  // Re-sync to the requested initial step every time the modal opens
  // (covers reopens after dismiss + return-from-Stripe lands on invite).
  useEffect(() => {
    if (isOpen) setStep(initialStep);
  }, [isOpen, initialStep]);

  if (!isOpen) return null;

  return (
    <ModalShell onClose={onClose}>
      {step === "card" ? (
        <CardStep
          reason={reason}
          onClose={onClose}
          onSuccess={() => {
            if (typeof onSuccess === "function") onSuccess();
            setStep("invite");
          }}
        />
      ) : (
        <InviteStep onDone={onClose} />
      )}
    </ModalShell>
  );
}

// ── Step 1: hand-off to Stripe Checkout ──────────────────────────────

function CardStep({ reason = "missing", onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleAddCard() {
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      // Return URL = current page + ?billing_setup=invite so
      // BillingSetupGate reopens the modal at the invite step on return.
      const returnUrl = `${window.location.origin}${window.location.pathname}?billing_setup=invite`;
      const resp = await createCheckoutSession({ returnUrl });
      if (!resp?.url) {
        throw new Error("Stripe returnerade ingen Checkout-URL.");
      }
      window.location.assign(resp.url);
    } catch (err) {
      const body = err?.body || err?.data;
      const friendly = err?.message && !/^HTTP \d/.test(err.message) ? err.message : null;
      const detail = body?.detail || friendly || "Kunde inte starta Stripe Checkout.";
      setError(detail);
      setSubmitting(false);
    }
  }

  return (
    <>
      <header style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
          {reason === "expired" ? "Kortet har gått ut" : "Kort saknas"}
        </h2>
        <p style={{ fontSize: 13, color: "#6b7280", marginTop: 6, lineHeight: 1.5 }}>
          {reason === "expired" ? (
            <>Ditt sparade kort har gått ut. Lägg till ett nytt så fortsätter
            aktiveringen och pågående prenumerationer utan avbrott.</>
          ) : (
            <>Lägg till ett betalkort innan workspace aktiveras. Du skickas
            vidare till Stripes säkra betalsida och kommer tillbaka hit när
            kortet är sparat.</>
          )}
        </p>
      </header>

      {error && (<div style={errorBoxStyle}>{error}</div>)}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          style={btnSecondary(submitting)}
        >
          Avbryt
        </button>
        <button
          type="button"
          onClick={handleAddCard}
          disabled={submitting}
          style={btnPrimary(submitting)}
        >
          {submitting ? "Skickar till Stripe…" : "Lägg till kort"}
        </button>
      </div>

      <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 14, lineHeight: 1.5 }}>
        Kortet sparas hos Stripe (PCI-DSS Level 1). Vi ser bara kortmärke
        och de sista fyra siffrorna.
      </p>
    </>
  );
}

// ── Step 2: invite teammates ────────────────────────────────────────

// Same role descriptions as BrokerSettings — keep wording in sync.
const ROLE_OPTIONS = [
  {
    value: "admin",
    label: "Administratör",
    hint: "Full åtkomst inklusive betalning och fakturahantering. Lägger till betalkort, aktiverar workspaces och bjuder in användare.",
  },
  {
    value: "full_access",
    label: "Full åtkomst",
    hint: "Samma som administratör förutom betalning. Kan aktivera workspaces om kort finns, men kan inte själv lägga till kort.",
  },
  {
    value: "member",
    label: "Medlem",
    hint: "Endast läsläge på de workspace de bjuds in till. Kan inte aktivera workspaces eller hantera medlemmar.",
  },
];

function _newInviteRow() {
  return { id: Math.random().toString(36).slice(2, 9), email: "", role: "full_access" };
}

function InviteStep({ onDone }) {
  const [rows, setRows] = useState([_newInviteRow()]);
  const [showRoleInfo, setShowRoleInfo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null); // { sent: n, failed: [{email, msg}] }
  const [error, setError] = useState("");

  function patchRow(id, patch) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }
  function addRow() { setRows((prev) => [...prev, _newInviteRow()]); }
  function removeRow(id) {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  }

  async function handleSend() {
    const valid = rows.filter((r) => /\S+@\S+\.\S+/.test(r.email.trim()));
    if (valid.length === 0) {
      setError("Lägg till minst en giltig e-postadress eller hoppa över.");
      return;
    }
    setError("");
    setSubmitting(true);
    const sent = [];
    const failed = [];
    for (const r of valid) {
      try {
        await inviteTeammate({ email: r.email.trim().toLowerCase(), role: r.role });
        sent.push(r.email);
      } catch (err) {
        const body = err?.body || err?.data;
        const friendly = err?.message && !/^HTTP \d/.test(err.message) ? err.message : null;
        const detail = body?.detail || friendly || "Okänt fel";
        failed.push({ email: r.email, msg: detail });
      }
    }
    setResults({ sent, failed });
    setSubmitting(false);
    if (failed.length === 0) {
      // Auto-close after a short pause so broker sees the success state.
      setTimeout(() => onDone?.(), 1200);
    }
  }

  return (
    <>
      <header style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Bjud in teamet</h2>
        <p style={{ fontSize: 13, color: "#6b7280", marginTop: 6, lineHeight: 1.5 }}>
          Lägg till kollegor som ska komma åt byråns workspaces. De får en
          mejlinbjudan med en länk för att skapa sitt konto. Du kan hoppa över
          och bjuda in senare från inställningarna.
        </p>
      </header>

      {results ? (
        <div style={{ fontSize: 13, color: "#374151" }}>
          {results.sent.length > 0 && (
            <div style={{ padding: "10px 12px", background: "#D1FAE5", borderRadius: 8, marginBottom: 8 }}>
              ✓ Inbjudningar skickade till {results.sent.length} {results.sent.length === 1 ? "person" : "personer"}.
            </div>
          )}
          {results.failed.length > 0 && (
            <div style={{ padding: "10px 12px", background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Misslyckades:</div>
              {results.failed.map((f) => (
                <div key={f.email} style={{ fontSize: 12 }}>
                  <strong>{f.email}:</strong> {f.msg}
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                <button type="button" onClick={onDone} style={btnPrimary(false)}>Klar</button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rows.map((r) => (
              <div key={r.id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="email"
                  placeholder="kollega@bolag.se"
                  value={r.email}
                  onChange={(e) => patchRow(r.id, { email: e.target.value })}
                  disabled={submitting}
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    fontSize: 13,
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                  }}
                />
                <select
                  value={r.role}
                  onChange={(e) => patchRow(r.id, { role: e.target.value })}
                  disabled={submitting}
                  style={{
                    padding: "8px 10px",
                    fontSize: 13,
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    background: "#fff",
                  }}
                >
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => removeRow(r.id)}
                  disabled={rows.length === 1 || submitting}
                  aria-label="Ta bort rad"
                  style={{
                    width: 28, height: 28,
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    background: "#fff",
                    color: rows.length === 1 ? "#d1d5db" : "#6b7280",
                    cursor: rows.length === 1 ? "not-allowed" : "pointer",
                    fontSize: 14,
                  }}
                >×</button>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
            <button
              type="button"
              onClick={addRow}
              disabled={submitting}
              style={{
                padding: "6px 12px",
                fontSize: 12,
                background: "transparent",
                color: "#374151",
                border: "1px dashed #9ca3af",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >+ Lägg till rad</button>

            <button
              type="button"
              onClick={() => setShowRoleInfo((v) => !v)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px",
                fontSize: 12,
                background: "transparent",
                color: "#6b7280",
                border: "1px solid #e5e7eb",
                borderRadius: 999,
                cursor: "pointer",
              }}
              aria-expanded={showRoleInfo}
            >
              ⓘ Vad betyder rollerna?
            </button>
          </div>

          {showRoleInfo && (
            <div style={{
              marginTop: 10,
              padding: "12px 14px",
              background: "#F5F5F4",
              border: "1px solid #E7E5E4",
              borderRadius: 8,
              fontSize: 12,
              color: "#374151",
            }}>
              {ROLE_OPTIONS.map((opt) => (
                <div key={opt.value} style={{ marginBottom: 6 }}>
                  <strong>{opt.label}:</strong> {opt.hint}.
                </div>
              ))}
            </div>
          )}

          {error && (<div style={errorBoxStyle}>{error}</div>)}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
            <button
              type="button"
              onClick={onDone}
              disabled={submitting}
              style={btnSecondary(submitting)}
            >
              Hoppa över
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={submitting}
              style={btnPrimary(submitting)}
            >
              {submitting ? "Skickar…" : "Skicka inbjudningar"}
            </button>
          </div>
        </>
      )}
    </>
  );
}

// ── Shared modal shell ──────────────────────────────────────────────

function ModalShell({ children, onClose }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={backdropStyle}
      onClick={(e) => { if (e.target === e.currentTarget && typeof onClose === "function") onClose(); }}
    >
      <div style={modalStyle}>{children}</div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────

const backdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalStyle = {
  background: "#fff",
  width: "min(480px, 92vw)",
  maxHeight: "90vh",
  overflowY: "auto",
  padding: 24,
  borderRadius: 12,
  boxShadow: "0 24px 56px rgba(0,0,0,0.25)",
};

const errorBoxStyle = {
  marginTop: 12,
  padding: "10px 12px",
  background: "#FEF2F2",
  border: "1px solid #FCA5A5",
  borderRadius: 8,
  fontSize: 13,
  color: "#991B1B",
};

const btnPrimary = (disabled) => ({
  padding: "10px 18px",
  fontSize: 13,
  fontWeight: 600,
  background: disabled ? "#9ca3af" : "#1C1917",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: disabled ? "wait" : "pointer",
});

const btnSecondary = (disabled) => ({
  padding: "10px 18px",
  fontSize: 13,
  fontWeight: 500,
  background: "#fff",
  color: disabled ? "#9ca3af" : "#374151",
  border: "1px solid #d1d5db",
  borderRadius: 8,
  cursor: disabled ? "not-allowed" : "pointer",
});
